<template>
  <div class="search-container">
    <div class="search-input-wrapper">
      <font-awesome-icon :icon="['fal', 'magnifying-glass']" class="search-icon" />
      <b-form-input
        :id="inputId"
        v-model="searchText"
        type="search"
        :placeholder="placeholder"
        autocomplete="off"
        class="search-input-with-icon"
        @input="onSearchInput"
      />
    </div>

    <div v-if="isSearching" class="search-dropdown">
      <div class="text-center py-3">
        <b-spinner small variant="primary" />
      </div>
    </div>

    <div v-else-if="visibleResults.length > 0" class="search-dropdown">
      <div
        v-for="employee in visibleResults"
        :key="employee.employee_id"
        class="search-dropdown__item"
        role="button"
        @mousedown.prevent="select(employee)"
      >
        <div class="search-dropdown__name">{{ employee.full_name }}</div>
        <div v-if="subtitleFor(employee)" class="search-dropdown__subtitle">{{ subtitleFor(employee) }}</div>
      </div>
    </div>

    <div v-else-if="searchText.length >= MIN_SEARCH_LENGTH" class="search-dropdown">
      <div class="text-muted py-3 px-3">No employees found</div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { recipientSubtitles } from '@/pages/recognition/utils/giveRecognition';

const MIN_SEARCH_LENGTH = 3;

const props = defineProps({
  programId: {
    type: String,
    default: null,
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
  excludeIds: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Find members',
  },
  inputId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['select']);

const searchText = ref('');
const searchResults = ref([]);
const isSearching = ref(false);

let debounceTimeout;
let searchRequestId = 0;

const candidates = computed(() => {
  const excluded = new Set(props.excludeIds);
  return searchResults.value.filter(e => !excluded.has(e.employee_id));
});

const visibleResults = computed(() => {
  const selected = new Set(props.selectedIds);
  return candidates.value.filter(e => !selected.has(e.employee_id));
});

const subtitles = computed(() => recipientSubtitles(candidates.value));

function subtitleFor(employee) {
  return subtitles.value.get(employee.employee_id) || '';
}

function clearSearch() {
  searchText.value = '';
  searchResults.value = [];
  isSearching.value = false;
  searchRequestId++;
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
}

function onSearchInput() {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  if (searchText.value.length < MIN_SEARCH_LENGTH) {
    searchResults.value = [];
    isSearching.value = false;
    searchRequestId++;
    return;
  }

  debounceTimeout = setTimeout(async () => {
    const requestId = ++searchRequestId;
    isSearching.value = true;
    try {
      const data = await ApiClient.searchRecognitionEmployees({
        searchText: searchText.value,
        programId: props.programId || undefined,
        limit: 10,
      });
      if (requestId === searchRequestId) {
        searchResults.value = data.data;
      }
    } catch {
      if (requestId === searchRequestId) {
        searchResults.value = [];
      }
    } finally {
      if (requestId === searchRequestId) {
        isSearching.value = false;
      }
    }
  }, 250);
}

function select(employee) {
  // Captured, not recomputed: the colliding group is this result set, which changes
  // per keystroke and is gone by the confirm step. Must be read before clearSearch.
  const subtitle = subtitleFor(employee);
  emit('select', subtitle ? { ...employee, picker_subtitle: subtitle } : employee);
  clearSearch();
}

onBeforeUnmount(clearSearch);

defineExpose({ clearSearch });
</script>

<style scoped lang="scss">
.search-container,
.search-input-wrapper {
  position: relative;
}

.search-dropdown {
  position: absolute;
  z-index: 1000;
  width: 100%;
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 15rem;
  overflow-y: auto;
  margin-top: 0.25rem;

  &__item {
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: var(--palette-color-default-lighten-30);
  }
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--palette-color-default-lighten-30);
  z-index: 1;
  pointer-events: none;
}

.search-input-with-icon {
  padding-left: 2.25rem;
}
</style>
